package co.maytes.standupbot.dashboard.features.settings

import co.maytes.standupbot.shared.dto.integration.HealthStateDto
import co.maytes.standupbot.shared.dto.integration.IntegrationDto
import co.maytes.standupbot.shared.dto.integration.IntegrationProviderDto
import co.maytes.standupbot.shared.dto.integration.SyncMembersResponseDto
import co.maytes.standupbot.shared.dto.integration.effectiveState
import co.maytes.standupbot.shared.dto.setup.SetupCheckReportDto
import co.maytes.standupbot.shared.dto.setup.summaryLabel
import co.maytes.standupbot.shared.util.RelativeTime
import kotlinx.datetime.Instant

enum class ServiceLevel { Connected, NeedsAttention, NotConnected }

data class ServiceSnapshot(val name: String, val level: ServiceLevel)

data class SubtitleParts(val strong: String, val plain: String)

object IntegrationsCopy {
    val services: List<IntegrationProviderDto> = listOf(
        IntegrationProviderDto.SLACK,
        IntegrationProviderDto.LINEAR,
        IntegrationProviderDto.GITHUB,
        IntegrationProviderDto.GOOGLE_CALENDAR,
    )

    const val CHECK_HINT =
        "Test my setup checks Slack, your standup channels, the schedule, Linear, your roster and any optional " +
            "integrations. It only reads; nothing is posted."

    fun displayName(provider: IntegrationProviderDto): String = when (provider) {
        IntegrationProviderDto.SLACK -> "Slack"
        IntegrationProviderDto.LINEAR -> "Linear"
        IntegrationProviderDto.GOOGLE_CALENDAR -> "Google Calendar"
        IntegrationProviderDto.GITHUB -> "GitHub"
        IntegrationProviderDto.ANTHROPIC -> "Anthropic"
        IntegrationProviderDto.UNKNOWN -> "Integration"
    }

    fun slug(provider: IntegrationProviderDto): String = when (provider) {
        IntegrationProviderDto.SLACK -> "slack"
        IntegrationProviderDto.LINEAR -> "linear"
        IntegrationProviderDto.GOOGLE_CALENDAR -> "google"
        IntegrationProviderDto.GITHUB -> "github"
        IntegrationProviderDto.ANTHROPIC -> "anthropic"
        IntegrationProviderDto.UNKNOWN -> "unknown"
    }

    fun tagline(provider: IntegrationProviderDto): String = when (provider) {
        IntegrationProviderDto.SLACK -> "Posts daily standups to your channels"
        IntegrationProviderDto.LINEAR -> "Reads issues, cycles, and projects"
        IntegrationProviderDto.GOOGLE_CALENDAR -> "Surfaces standup calendar events in the standup"
        IntegrationProviderDto.GITHUB -> "Pulls merged PRs, reviews, and issues for each standup member"
        IntegrationProviderDto.ANTHROPIC -> "Writes the summaries from your own API key"
        IntegrationProviderDto.UNKNOWN -> ""
    }

    fun disconnectBody(provider: IntegrationProviderDto): String = when (provider) {
        IntegrationProviderDto.SLACK ->
            "StandupBot will stop posting to Slack and your standups will be paused until you reconnect Slack."
        IntegrationProviderDto.GITHUB ->
            "StandupBot will stop reading commits and pull requests from GitHub for this workspace. " +
                "To remove its access completely, also uninstall the StandupBot app on GitHub " +
                "(GitHub Settings, Applications, Installed GitHub Apps)."
        else ->
            "StandupBot will stop reading activity from ${displayName(provider)} for this workspace. " +
                "Standups that depend on this integration will fail until you reconnect."
    }

    fun level(integration: IntegrationDto?, suspended: Boolean): ServiceLevel = when {
        integration == null -> ServiceLevel.NotConnected
        suspended -> ServiceLevel.NeedsAttention
        else -> when (integration.effectiveState()) {
            HealthStateDto.HEALTHY -> ServiceLevel.Connected
            HealthStateDto.DEGRADED, HealthStateDto.NEEDS_RECONNECT -> ServiceLevel.NeedsAttention
            HealthStateDto.NOT_CONFIGURED -> ServiceLevel.NotConnected
        }
    }

    fun subtitle(services: List<ServiceSnapshot>): SubtitleParts {
        val connected = services.count { it.level == ServiceLevel.Connected }
        val tally = "$connected of ${services.size}"
        val attention = services.filter { it.level == ServiceLevel.NeedsAttention }.map { it.name }
        return if (attention.isEmpty()) {
            SubtitleParts(strong = tally, plain = " services connected.")
        } else {
            SubtitleParts(strong = attentionPhrase(attention), plain = ". $tally services connected.")
        }
    }

    fun checkFacts(report: SetupCheckReportDto?, now: Instant): List<Pair<String, String>> =
        if (report == null) {
            emptyList()
        } else {
            listOf("Last check" to RelativeTime.format(report.ranAt, now), "Result" to report.summaryLabel())
        }

    fun compact(text: String): String =
        text.split(" ").joinToString(" ") { word ->
            if (word.length > MAX_WORD) word.take(HEAD) + "…" + word.takeLast(TAIL) else word
        }

    fun syncCaption(lastSyncAt: Instant?, lastEventCount: Int?, now: Instant): String {
        if (lastSyncAt == null) return "Not synced yet"
        val events = lastEventCount ?: 0
        val noun = if (events == 1) "event" else "events"
        return "Last synced ${RelativeTime.format(lastSyncAt, now)} · $events $noun"
    }

    fun syncSummary(response: SyncMembersResponseDto): String {
        val parts = buildList {
            response.linear?.let { add("Linear: ${it.linked + it.linkedByName + it.alreadyLinked} linked") }
            response.github?.takeIf { it.enabled }?.let { add("GitHub: ${it.linked + it.skippedAlreadyLinked} linked") }
        }
        return if (parts.isEmpty()) "No providers to sync" else parts.joinToString(" · ")
    }

    private fun attentionPhrase(names: List<String>): String = when (names.size) {
        1 -> "${names.single()} needs attention"
        2 -> "${names[0]} and ${names[1]} need attention"
        else -> "${names.size} services need attention"
    }

    private const val MAX_WORD = 28
    private const val HEAD = 10
    private const val TAIL = 10
}
